import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-base leading-none font-semibold tracking-[-0.16px] transition-[background-color,box-shadow,filter] duration-150 ease-in-out focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-[#7C49EF] text-white rounded-md border-b border-b-[#4A2C8F] shadow-none hover:bg-[#633ABF] active:bg-[#7C49EF]",
        destructive:
          "bg-[#DC2828] text-white border-b border-b-[#A21E1E] shadow-none hover:bg-[#A21E1E] active:bg-[#8B1919] rounded-xs",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-white text-[#30313D] rounded-xs border border-[#D6DCE4] shadow-none hover:border-[#A4A4A4] active:bg-[#f5f5f5]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        ios: "bg-[#7C49EF] text-white rounded-xs border-b border-b-[#4A2C8F] shadow-none hover:bg-[#633ABF] active:bg-[#7C49EF]",
        "ios-secondary":
          "bg-white text-[#30313D] rounded-xs border border-[#D6DCE4] shadow-none hover:border-[#A4A4A4] active:bg-[#f5f5f5]",
        "ios-ghost": "text-primary hover:bg-accent/50",
        fab: "bg-[#7C49EF] text-white rounded-full border-b border-b-[#4A2C8F] shadow-lg hover:bg-[#633ABF] active:bg-[#7C49EF]",
      },
      size: {
        default: "p-2",
        sm: "p-2",
        lg: "px-4 py-2 text-lg [&_svg]:w-4 [&_svg]:h-4 [&_svg]:stroke-[2]",
        icon: "h-11 w-11",
        "icon-sm": "h-9 w-9 rounded-lg",
        fab: "h-14 w-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
